import {
  LockClosedIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { Formik, Form } from "formik";
import { Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormikCheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { MoneyInput } from "@/core/components/money-input";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business/use-business";
import { parseMaskedDollarInput } from "@/core/utils/money";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { useGetAccountSummary } from "@/internal-transactions/query";
import { useSendPayout } from "@/payouts/query";
import { REFERRAL_COLUMN_TO_STATUS } from "@/referrals/constants";
import { useUpdateReferral } from "@/referrals/query";
import { IReferral } from "@/referrals/referral";

interface PayoutModalProps {
  onOpenChange: (referral: IReferral | null) => void;
  referral: IReferral | null;
  onFinalPayout?: (referralId: string) => void;
}

const createPayoutSchema = (requiresDealSize: boolean) => {
  const baseSchema = {
    amount: Yup.string()
      .required("Amount is required")
      .test("min-value", "Amount must be at least 1", (value) => {
        if (!value) return false;
        const num = parseMaskedDollarInput(value);
        return num !== null && num >= 1;
      }),
    confirm_payout: Yup.boolean()
      .required("You must confirm the payout")
      .oneOf([true], "You must confirm the payout"),
    notes: Yup.string().optional(),
    customer_message: Yup.string().optional(),
  };

  if (requiresDealSize) {
    return Yup.object().shape({
      ...baseSchema,
      deal_size: Yup.string()
        .required("Deal size is required")
        .test("valid-amount", "Deal size must be a valid amount", (value) => {
          if (!value) return false;
          const num = parseMaskedDollarInput(value);
          return num !== null && num > 0;
        }),
    });
  }

  return Yup.object().shape(baseSchema);
};

export const PayoutModal = ({
  onOpenChange,
  referral,
  onFinalPayout,
}: PayoutModalProps) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { businessId } = useBusiness();

  const { data: business } = useGetBusiness(businessId);
  const { data: accountSummary, isLoading: isAccountSummaryLoading } =
    useGetAccountSummary(businessId);

  const { id: referralId = "" } = referral || {};
  const { mutate: sendPayout } = useSendPayout(referralId);
  const { mutate: updateReferral } = useUpdateReferral(businessId);

  const isClosed = referral?.status === REFERRAL_COLUMN_TO_STATUS.closed;
  const requiresDealSize = !referral?.deal_size;

  return (
    <Dialog
      open={!!referral}
      onOpenChange={(open) => onOpenChange(open ? referral : null)}
    >
      <DialogContent description="Send payout to customer">
        <Formik
          initialValues={{
            amount: "",
            confirm_payout: false,
            final_payout: false,
            notes: "",
            customer_message: "",
            deal_size: "",
          }}
          validationSchema={createPayoutSchema(requiresDealSize)}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            if (requiresDealSize && values.deal_size) {
              const parsedDealSize = parseMaskedDollarInput(values.deal_size);
              if (parsedDealSize !== null && parsedDealSize > 0) {
                updateReferral({
                  id: referralId,
                  updates: { deal_size: parsedDealSize },
                });
              }
            }

            sendPayout(
              {
                ...values,
                amount: Math.round(
                  (parseMaskedDollarInput(values.amount) ?? 0) * 100,
                ),
              },
              {
                onSuccess: () => {
                  onOpenChange(null);
                  if (values.final_payout) {
                    onFinalPayout?.(referralId);
                  }
                  posthog?.capture("payout_sent", {
                    referral_id: referralId,
                    amount: values.amount,
                    final_payout:
                      referral?.status === REFERRAL_COLUMN_TO_STATUS.closed,
                  });
                  toast.success("Payout sent");
                  setSubmitting(false);
                },
                onError: () => {
                  posthog?.capture("payout_failed", {
                    referral_id: referralId,
                    amount: values.amount,
                    final_payout:
                      referral?.status === REFERRAL_COLUMN_TO_STATUS.closed,
                  });
                  toast.error("An error occurred while sending the payout");
                  setSubmitting(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            getFieldProps,
            setFieldValue,
          }) => {
            const walletBalance = accountSummary?.wallet_balance;
            const payoutAmount = parseMaskedDollarInput(values.amount) ?? 0;
            const hasInsufficientFunds =
              !isAccountSummaryLoading &&
              walletBalance != null &&
              payoutAmount > Number(walletBalance);
            const shouldDisableSubmit =
              isSubmitting ||
              !isValid ||
              isAccountSummaryLoading ||
              hasInsufficientFunds;

            return (
              <Form className="flex flex-col">
                <DialogHeader className="border border-stroke-tertiary bg-green-10">
                  <DialogTitle>Send Payout</DialogTitle>
                </DialogHeader>
                <ScrollArea className="max-h-[70vh] w-full">
                  <div className="max-h-full">
                    {referral && !isClosed && (
                      <Stack
                        direction="row"
                        align="center"
                        className="px-6 pt-6"
                        gap="sm"
                      >
                        <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-negative-text" />
                        <Typography variant="bodyW2" className="leading-5">
                          Heads up, you're about to pay a referral bonus for a
                          referral that hasn't closed yet.{" "}
                        </Typography>
                      </Stack>
                    )}

                    {hasInsufficientFunds &&
                      values.amount &&
                      !isAccountSummaryLoading && (
                        <Stack
                          direction="row"
                          align="center"
                          className="px-6 pt-6"
                          gap="sm"
                        >
                          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-negative-text" />
                          <Typography variant="bodyW2" className="leading-5">
                            You don't have sufficient funds for this payout.{" "}
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/business-settings/${businessId}/payments`,
                                )
                              }
                              className="text-primary underline hover:text-primary/80"
                            >
                              Would you like to add funds?
                            </button>
                          </Typography>
                        </Stack>
                      )}

                    {isAccountSummaryLoading && (
                      <Stack
                        direction="row"
                        align="center"
                        className="px-6 pt-6"
                        gap="sm"
                      >
                        <Loader2 className="h-6 w-6 animate-spin text-tertiary-text" />
                        <Typography variant="bodyW2" className="leading-5">
                          Loading account balance...
                        </Typography>
                      </Stack>
                    )}

                    <Stack className="p-6" gap="lg" fullWidth>
                      <Stack gap="sm">
                        <Typography variant="subtitleW3" textColor="tertiary">
                          Paying out to
                        </Typography>
                        <Typography variant="subtitleW2">
                          {getCustomerNameAndPhone(referral?.advocate)}
                        </Typography>
                      </Stack>

                      {requiresDealSize && (
                        <MoneyInput
                          name="deal_size"
                          label="Deal Size"
                          value={values.deal_size}
                          onChange={(value) =>
                            setFieldValue("deal_size", value)
                          }
                          error={
                            touched.deal_size ? errors.deal_size : undefined
                          }
                          required
                          fullWidth
                          autoFocus
                        />
                      )}

                      <MoneyInput
                        name="amount"
                        label="Amount"
                        value={values.amount}
                        onChange={(value) => setFieldValue("amount", value)}
                        error={
                          touched.amount && values.amount
                            ? errors.amount
                            : undefined
                        }
                        info={
                          business?.max_payout
                            ? `The advertised maximum payout is $${business.max_payout}`
                            : ""
                        }
                        required
                        autoFocus={!requiresDealSize}
                        fullWidth
                      />

                      <Textarea
                        placeholder="Send a message to the customer"
                        label="Customer message"
                        {...getFieldProps("customer_message")}
                      />
                      <Textarea
                        placeholder="Enter any internal notes here"
                        label="Notes"
                        {...getFieldProps("notes")}
                      />
                      <Stack
                        direction="row"
                        align="center"
                        gap="sm"
                        className="mt-[-16px]"
                      >
                        <LockClosedIcon className="h-6 w-6 text-tertiary-text" />
                        <Typography variant="captionW1" textColor="tertiary">
                          Your notes are only visible internally. They will not
                          be shared with the customer.
                        </Typography>
                      </Stack>

                      <FormikCheckboxWithLabel
                        id="confirmPayout"
                        label="I understand that I am sending funds from the company account to a customer via SMS.*"
                        checked={values.confirm_payout}
                        onCheckedChange={(checked) =>
                          setFieldValue("confirm_payout", checked)
                        }
                      />

                      <FormikCheckboxWithLabel
                        id="finalPayout"
                        label="This is the final payout for this referral"
                        checked={values.final_payout}
                        onCheckedChange={(checked) =>
                          setFieldValue("final_payout", checked)
                        }
                      />
                    </Stack>
                  </div>
                </ScrollArea>
                <DialogFooter>
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => onOpenChange(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={shouldDisableSubmit}
                    leftIcon={
                      isSubmitting && <Loader2 className="animate-spin" />
                    }
                  >
                    Send Payment
                  </Button>
                </DialogFooter>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
